import { Box, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { Controller, type Control, type FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ConsentControlledSwitch } from "../ConsentControlledSwitch";
import { ConsentRichTextField } from "../ConsentRichTextField";
import { ConsentInputSkeleton } from "./ConsentFormShared";
import { type ConsentItemFormData, type ConsentItemLocalizedDraft } from "../utils/consentItemModalModel";
import type { Locale } from "src/api/locales/useLocales";

type ConsentItemGlobalFieldsProps = {
    control: Control<ConsentItemFormData>;
    errors: FieldErrors<ConsentItemFormData>;
    isGlobalContentLoading: boolean;
    itemType: "document" | "opt_in";
};

type ConsentItemLocalizedFieldsProps = {
    consentItemId?: number;
    control: Control<ConsentItemFormData>;
    editingLocale: string;
    errors: FieldErrors<ConsentItemFormData>;
    hasAnyLocalizedConsentItemField: boolean;
    isLocalizedContentFetching: boolean;
    isLocalizedContentLoading: boolean;
    itemType: "document" | "opt_in";
    localeOptions: Array<Locale & { code: string }>;
    mutationIsLoading: boolean;
    open: boolean;
    onEditingLocaleChange: (nextLocale: string) => void;
    onLocalizedDraftChange: (patch: Partial<Pick<ConsentItemLocalizedDraft, "description" | "name">>) => void;
};

export const ConsentItemGlobalFields = ({
    control,
    errors,
    isGlobalContentLoading,
    itemType,
}: ConsentItemGlobalFieldsProps) => {
    const { t } = useTranslation();

    return (
        <Stack gap={2}>
            <Box>
                <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: "0.08em" }}>
                    {t("settings.consent.form.global_title", "Global settings")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t(
                        "settings.consent.form.item_global_description",
                        "Shared across every language for this consent item.",
                    )}
                </Typography>
            </Box>
            {isGlobalContentLoading ?
                <>
                    <ConsentInputSkeleton label={t("settings.consent.form.loading_item", "Loading consent item...")} />
                    <ConsentInputSkeleton label={t("settings.consent.form.loading_item", "Loading consent item...")} />
                </>
            :   <>
                    <Controller
                        name="code"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label={t("settings.consent.item.code", "Code")}
                                error={Boolean(errors.code)}
                                helperText={errors.code?.message}
                                fullWidth
                            />
                        )}
                    />
                </>
            }
            {itemType === "opt_in" && isGlobalContentLoading && (
                <ConsentInputSkeleton label={t("settings.consent.form.loading_item", "Loading consent item...")} />
            )}
            {itemType === "opt_in" && !isGlobalContentLoading && (
                <Controller
                    name="category"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label={t("settings.consent.item.category", "Category")}
                            error={Boolean(errors.category)}
                            helperText={errors.category?.message}
                            fullWidth
                        />
                    )}
                />
            )}
            {isGlobalContentLoading ?
                <ConsentInputSkeleton label={t("settings.consent.form.loading_item", "Loading consent item...")} />
            :   <Controller
                    name="active"
                    control={control}
                    render={({ field }) => (
                        <ConsentControlledSwitch
                            checked={field.value}
                            label={t("settings.consent.item.active", "Visible to users")}
                            onChange={field.onChange}
                        />
                    )}
                />
            }
        </Stack>
    );
};

export const ConsentItemLocalizedFields = ({
    consentItemId,
    control,
    editingLocale,
    errors,
    hasAnyLocalizedConsentItemField,
    isLocalizedContentFetching,
    isLocalizedContentLoading,
    itemType,
    localeOptions,
    mutationIsLoading,
    onEditingLocaleChange,
    onLocalizedDraftChange,
    open,
}: ConsentItemLocalizedFieldsProps) => {
    const { t } = useTranslation();

    return (
        <Stack gap={2}>
            <Box>
                <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: "0.08em" }}>
                    {t("settings.consent.form.localized_title", {
                        defaultValue: "Translated content ({{locale}})",
                        locale: editingLocale.toUpperCase(),
                    })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t(
                        "settings.consent.form.localized_description",
                        "These fields change with the selected content language.",
                    )}
                </Typography>
            </Box>
            {localeOptions.length > 0 && (
                <TextField
                    select
                    label={t("settings.consent.locale.label", "Content language")}
                    value={editingLocale}
                    disabled={mutationIsLoading || isLocalizedContentFetching}
                    onChange={(event) => onEditingLocaleChange(event.target.value)}
                    fullWidth
                >
                    {localeOptions.map((option) => (
                        <MenuItem key={option.code} value={option.code}>
                            {`${option.nativeName || option.name || option.code} (${option.code})`}
                        </MenuItem>
                    ))}
                </TextField>
            )}
            {consentItemId && !isLocalizedContentLoading && !hasAnyLocalizedConsentItemField && (
                <Typography variant="body2" color="text.secondary">
                    {t(
                        "settings.consent.item.translation_fallback",
                        "No dedicated translation exists for this language yet. Saving will create or update this translation.",
                    )}
                </Typography>
            )}
            {isLocalizedContentLoading ?
                <ConsentInputSkeleton
                    label={t("settings.consent.form.loading_content", "Loading translated content...")}
                />
            :   <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label={t("settings.consent.item.name", "Name")}
                            error={Boolean(errors.name)}
                            helperText={errors.name?.message}
                            onChange={(event) => {
                                field.onChange(event);
                                onLocalizedDraftChange({ name: event.target.value });
                            }}
                            fullWidth
                        />
                    )}
                />
            }
            <Controller
                name="description"
                control={control}
                render={({ field }) => (
                    <ConsentRichTextField
                        editorKey={`consent-item-description-${consentItemId ?? "new"}-${editingLocale}-${open ? "open" : "closed"}`}
                        id="consent-item-description"
                        label={t("settings.consent.item.description", "Internal description")}
                        error={Boolean(errors.description)}
                        helperText={errors.description?.message}
                        loading={isLocalizedContentLoading}
                        loadingLabel={t("settings.consent.form.loading_content", "Loading translated content...")}
                        minHeight={160}
                        onChange={(value) => {
                            field.onChange(value);
                            onLocalizedDraftChange({ description: value });
                        }}
                        value={field.value}
                    />
                )}
            />
            <Box>
                <Typography variant="body" color="text.secondary">
                    {itemType === "document" ?
                        t(
                            "settings.consent.item.document_hint",
                            "Documents are treated as required contractual items in the current product configuration.",
                        )
                    :   t(
                            "settings.consent.item.preference_hint",
                            "Preferences remain optional and feed the user preference center.",
                        )
                    }
                </Typography>
            </Box>
        </Stack>
    );
};
